import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { css } from "@emotion/react";
import { useState, useEffect } from "react";

import { Image } from "src/components/CoverPhotoOrVideoPicker/styles";
import { MAX_INPUT_WIDTH } from "src/components/Nonprofit/EditForm/shared";
import { getWindow } from "src/utility/window";

const youtubeEmbedPlaceholderImageCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 2 / 1;
  padding-top: 0;
`;

const youtubeEmbedIframeConteinerCss = css`
  width: 100%;
  max-width: ${MAX_INPUT_WIDTH};
  aspect-ratio: 2 / 1;
  border-radius: 8px;
  overflow: hidden;
`;

export const YouTubeEmbedPlaceholder = () => (
  <Image url={null} css={youtubeEmbedPlaceholderImageCss}>
    <Icon
      size={IconSize.XXX_LARGE}
      css={{ opacity: 0.3 }}
      display={IconDisplay.SECONDARY}
      iconImport={() => import("@components/Icon/icons/YoutubeLogoCircleIcon")}
    />
  </Image>
);

interface YouTubeEmbedProps {
  videoId: string;
  onError: (errorMessage?: string) => void;
}

export function YouTubeEmbed(props: YouTubeEmbedProps) {
  useYouTubePlayer(props);

  return (
    <div css={youtubeEmbedIframeConteinerCss}>
      <div id="yt-player" />
    </div>
  );
}

function useYouTubePlayer({
  videoId,
  onError,
}: {
  videoId: string;
  onError: (msg?: string) => void;
}) {
  const window = getWindow();
  const [player, setPlayer] = useState<any>();
  const [iframeApiReady, setIframeApiReady] = useState(false);

  // Load YouTube Iframe API
  useEffect(() => {
    if (!window) {
      return;
    }
    // If the API is already loaded, set the state to true and do not load it again
    if (window.YT) {
      setIframeApiReady(true);
      return;
    }
    const tag = window.document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = window.document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    window.onYouTubeIframeAPIReady = () => {
      setIframeApiReady(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create YouTube player
  useEffect(() => {
    if (!window?.YT || !iframeApiReady) {
      return;
    }
    // Destroy the previous player
    // to avoid multiple player instances
    player?.destroy();
    setPlayer(
      new window.YT.Player("yt-player", {
        height: "100%",
        width: "100%",
        videoId,
        events: {
          onReady: (event: any) => {
            // use cueVideoById insted of loadVideoById to avoid autoplay
            event.target.cueVideoById(videoId);
          },
          onError: () => {
            onError("YouTube video is not available");
          },
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeApiReady, videoId]);
}
